<template>
  <div>
    <!-- Player Info Component -->
    <PlayerInfo :rider-data="rider_default_data" :genders="genders" />

    <!-- Tab Navigation Component -->
    <TabNavigation
      :is-active-profile-tab="isActiveProfileTab"
      :is-active-activity-tab="isActiveActivityTab"
      :is-active-in-game-shop-tab="isActiveInGameShopTab"
      @tab-changed="makeTabFunc"
    />

    <section class="tab-content">
      <div class="container main-tab-container p-0">
        <div class="tab-content-wrpr">
          <div class="tab-content" id="nav-tabContent">
            <!-- Overview Component (Already existing) -->
            <OverView
              :is-active="isActiveProfileTab"
              :third-party-data="rider_default_data?.third_party_data"
              :user-data="rider_default_data?.user?.userData"
              :total-events="user_total_event_count"
            />

            <!-- Activities Tab Content -->
            <div
              class="tab-pane fade"
              :class="{ 'show active': isActiveActivityTab }"
              id="nav-activities"
              role="tabpanel"
              aria-labelledby="nav-activities-tab"
              tabindex="1"
            >
              <!-- When not showing detail -->
              <div v-if="!showDetail">
                <!-- Power Curves Component -->
                <PowerCurves
                  :chart-data-default="chartDataDefault"
                  :chart-options="chartOptions"
                  :chart-data-weight-per-kg="chartDataWeightPerKg"
                  :chart-options-weight-per-kg="chartOptionsWeightPerKg"
                  :first-graph-date="firstGraphDate"
                  :second-graph-date="secondGraphDate"
                  :tab-title="tabTitle"
                  @tab-selected="selectTab"
                />

                <!-- Activity Filters Component -->
                <ActivityFilters
                  :type="type"
                  :sortDate="sortDate"
                  @update:type="(val) => (type = val)"
                  @update:sortDate="(val) => (sortDate = val)"
                />

                <!-- Activity List Component -->
                <ActivityList
                  v-if="activities.results.length > 0"
                  :activities="activities"
                  :genders="genders"
                  :rider-data="rider_default_data"
                  :graph-file-ids="graphFileIds"
                  :finished="finished"
                  @view-activity="getSingleActivity"
                  @download-file="downloadFile"
                  @add-to-graph="addToGraph"
                  @load-more="getAllActivities"
                />

                <div v-else>
                  <div class="msg-txt">
                    <p>- All your activities will be displayed here -</p>
                  </div>
                </div>
              </div>

              <!-- When showing detail -->
              <ActivityDetail
                v-if="showDetail"
                :single-ride-data="single_ride_data"
                :rider-data="rider_default_data"
                :genders="genders"
                :selected-date="selectedDate"
                :selected-file-id="selectedFileId"
                :chart-options-elevation="chartOptionsElevation"
                :chart-data-elevation="chartDataElevation"
                :chart-options-power="chartOptionsPower"
                :chart-data-power="chartDataPower"
                :chart-options-speed="chartOptionsSpeed"
                :chart-data-speed="chartDataSpeed"
                :chart-options-heart-rate="chartOptionsHeartRate"
                :chart-data-heart-rate="chartDataHeartRate"
                :chart-options-cadence="chartOptionsCadence"
                :chart-data-cadence="chartDataCadence"
                :hover-elevation-date="hoverElevationDate"
                :hover-elevation-time="hoverElevationTime"
                :hover-elevation="hoverElevation"
                :hover-power="hoverPower"
                :hover-speed="hoverSpeed"
                :hover-heart-rate="hoverHeartRate"
                :hover-cadence="hoverCadence"
                :avg-power="avgPower"
                :min-power="minPower"
                :max-power="maxPower"
                :avg-speed="avgSpeed"
                :max-speed="maxSpeed"
                :avg-heart-rate="avgHeartRate"
                :max-heart-rate="maxHeartRate"
                :min-heart-rate="minHeartRate"
                :avg-cadence="avgCadence"
                :max-cadence="maxCadence"
                @hide-detail="hideDetail"
                @download-file="downloadFile"
              />
            </div>

            <!-- Game Shop Component (Already existing) -->
            <GameShop
              :is-active="isActiveInGameShopTab"
              :user-gems="
                rider_default_data?.user?.userData['data']['PlayerData'][
                  'PlayerPersonalStruct'
                ]['Gems']
              "
              :user-coins="
                rider_default_data?.user?.userData['data']['PlayerData'][
                  'PlayerPersonalStruct'
                ]['Coins']
              "
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

// Import components
import OverView from "@/components/rider/OverView.vue";
import GameShop from "@/components/game_shop/GameShop";
import PlayerInfo from "@/components/player/PlayerInfo.vue";
import TabNavigation from "@/components/player/TabNavigation.vue";
import PowerCurves from "@/components/player/PowerCurves.vue";
import ActivityFilters from "@/components/player/ActivityFilters.vue";
import ActivityList from "@/components/player/ActivityList.vue";
import ActivityDetail from "@/components/player/ActivityDetail.vue";

// Import mixins and services
import chartMixin from "@/mixins/chartMixin";
import activityService from "@/services/activity-service";

export default {
  name: "PlayerActivities",
  head: {
    link: [
      {
        rel: "stylesheet",
        href: "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.2/font/bootstrap-icons.min.css",
      },
      {
        rel: "stylesheet",
        href: "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css",
      },
      { rel: "stylesheet", href: "/assets/css/activities/style.css" },
    ],
  },
  mixins: [chartMixin],
  components: {
    GameShop,
    OverView,
    PlayerInfo,
    TabNavigation,
    PowerCurves,
    ActivityFilters,
    ActivityList,
    ActivityDetail,
  },
  data() {
    return {
      // Activity state
      selectedDate: "",
      selectedFileId: "",
      showDetail: false,
      genders: ["MEN", "FEMALE", "Both"],
      graphFileIds: [],

      // Pagination state
      page: 1,
      type: "",
      finished: false,
      sortDate: "DESC",

      // Tab state
      isActiveProfileTab: true,
      isActiveActivityTab: false,
      isActiveInGameShopTab: false,
    };
  },
  computed: {
    ...mapGetters("user", [
      "activities",
      "compare_data",
      "single_ride_data",
      "rider_default_data",
      "user_total_event_count",
    ]),
  },
  async mounted() {
    this.showTab();
    await this.getAllActivities();
    await this.getDefaultData();
  },
  methods: {
    makeTabFunc(evt) {
      const tabclass = document.querySelector(".main-tab-container");

      if (evt.target.attributes.id.value === "nav-home-tab") {
        tabclass.classList.remove("container-fluid");
        tabclass.classList.add("container");
        this.isActiveProfileTab = true;
        this.isActiveActivityTab = false;
        this.isActiveInGameShopTab = false;
      } else if (evt.target.attributes.id.value === "nav-contact-tab3") {
        tabclass.classList.remove("container");
        tabclass.classList.add("container-fluid");
        this.isActiveProfileTab = false;
        this.isActiveActivityTab = true;
        this.isActiveInGameShopTab = false;
      } else if (evt.target.attributes.id.value === "nav-in-game-shop-tab3") {
        this.isActiveProfileTab = false;
        this.isActiveActivityTab = false;
        this.isActiveInGameShopTab = true;
      }
    },

    showTab() {
      const hash = window.location.hash;

      if (hash === "#profile") {
        this.isActiveProfileTab = true;
        this.isActiveActivityTab = false;
        this.isActiveInGameShopTab = false;
        document.getElementById("nav-home-tab").click();
      } else if (hash === "#activities") {
        this.isActiveProfileTab = false;
        this.isActiveActivityTab = true;
        this.isActiveInGameShopTab = false;
        document.getElementById("nav-contact-tab3").click();
      } else if (hash === "#in-game-shop") {
        this.isActiveProfileTab = false;
        this.isActiveActivityTab = false;
        this.isActiveInGameShopTab = true;
        document.getElementById("nav-in-game-shop-tab3").click();
      } else {
        this.isActiveProfileTab = true;
        this.isActiveActivityTab = false;
        this.isActiveInGameShopTab = false;
      }
    },

    addToGraph(evt, fileId) {
      if (this.graphFileIds.indexOf(fileId) === -1) {
        evt.target.classList.add("active");
        this.graphFileIds.push(fileId);
      } else {
        evt.target.classList.remove("active");
        this.graphFileIds.splice(this.graphFileIds.indexOf(fileId), 1);
      }
    },

    async getDefaultData() {
      await activityService.getDefaultData(this.$store, (data) => {
        const powerData = data?.overall_average_graph?.power;
        const powerWeightData = data?.overall_average_graph?.power_weight;
        this.updateChartData(powerData, powerWeightData);
      });
    },

    async getAllActivities() {
      const result = await activityService.getAllActivities(this.$store, {
        type: this.type,
        page: this.page,
        sortDate: this.sortDate,
      });

      if (result.success) {
        if (result.isLastPage) {
          this.finished = true;
        } else {
          this.page++;
        }
      }
    },

    async downloadFile(fileId) {
      await activityService.downloadFile(this.$store, fileId);
    },

    async getSingleActivity(fileId, fileDate) {
      const result = await activityService.getSingleActivity(
        this.$store,
        fileId,
        (data) => {
          const success = this.processActivityData(data, fileId, fileDate);
          if (success) {
            this.showDetail = true;
            return { success: true };
          }
          return { success: false };
        }
      );

      if (!result.success) {
        global.app.$toastr.error(
          result.message || "Unable to fetch activity detail."
        );
      }
    },

    hideDetail() {
      this.showDetail = false;
    },
  },
  watch: {
    $route() {
      this.showTab();
      this.showDetail = false;
    },

    async graphFileIds() {
      this.resetChartData();

      if (this.graphFileIds.length > 2) {
        this.graphFileIds.shift();
        Array.from(
          document.querySelectorAll(".input_compare")
        )[0].classList.remove("active");
      }

      if (this.graphFileIds.length === 0) {
        this.getDefaultData();
      }

      if (this.graphFileIds.length > 0 && this.graphFileIds.length <= 2) {
        await activityService.getCompareData(this.$store, this.graphFileIds);
        // Process compare data here if needed
      }
    },

    type() {
      this.finished = false;
      this.page = 1;
      this.getAllActivities();
    },

    sortDate() {
      this.finished = false;
      this.page = 1;
      this.getAllActivities();
    },
  },
};
</script>

<style scoped>
@media (min-width: 1920px) {
  .container {
    max-width: 1600px;
    margin: 0 auto;
  }
}
</style>
